/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import {
  Config,
  setImageParamsKeyMap,
  setGlobalLocale,
} from '@adobe-commerce/elsie/lib';

type Listener = { off(): void };

type Listeners<T> = (config?: T) => Array<Listener | undefined>;

type Init<T> = (config?: T) => Promise<void>;

type Options<T> = { init: Init<T>; listeners: Listeners<T> };

export type Model<T = any, D = any> = {
  transformer?: (data: D) => T & { [key: string]: any };
};

/**
 * The `Initializer` class is responsible for setting up event listeners and initializing a module with the given configuration.
 *
 * @template T - The type of the configuration object.
 * @class
 */
export class Initializer<T> {
  private _listeners: Listener[] = [];
  listeners: Listeners<T>;
  init: Init<T>;
  config = new Config<T>({} as T);

  /**
   * Creates an instance of Initializer.
   * @param options - The initialization options.
   * @param options.init - A function that initializes the module.
   * @param options.listeners - A function that sets up event listeners.
   */
  constructor({ init, listeners }: Options<T>) {
    this.listeners = (config) => {
      // Unbind existing listeners
      this._listeners.forEach((listener) => listener.off());
      // Bind new listeners
      return (this._listeners = listeners(config) as Listener[]);
    };

    this.init = (options) => {
      const { imageParamsKeyMap, globalLocale, ...rest } =
        options as any;
      this.config.setConfig({ ...this.config.getConfig(), ...rest });
      setImageParamsKeyMap(imageParamsKeyMap);
      setGlobalLocale(globalLocale);
      return init(options);
    };
  }
}

type Initializers = [Initializer<any>, { [key: string]: any } | undefined][];

/**
 * The Initializers class provides methods to register, mount, and configure initializers.
 *
 * @class
 *
 * @method register - Registers a new initializer. If the initializers have already been mounted, it immediately binds the event listeners and initializes the API for the new initializer.
 * @method mount - Mounts all registered initializers. This involves binding the event listeners and initializing the APIs for each initializer, in that order.
 * @method setImageParamKeys - Sets the image parameter keys. These keys are used when initializing the APIs for the initializers.
 */
export class initializers {
  static _initializers: Initializers = [];
  static _mounted: boolean = false;
  static _imageParamsKeyMap: { [key: string]: string } | undefined = undefined;
  static _globalLocale: string | undefined = undefined;
  /**
   * Registers a new initializer. If the initializers have already been mounted,it immediately binds the event listeners and initializes the API for the new initializer.
   * @param initializer - The initializer to register.
   * @param options - Optional configuration for the initializer.
   */
  static register(
    initializer: Initializer<any>,
    options?: { [key: string]: any }
  ) {
    if (initializers._mounted) {
      initializer.listeners?.(options);
      initializer.init?.(options);
    }
    initializers._initializers.push([initializer, options]);
  }

  /**
   * Mounts the provided initializer immediately. This involves binding the event listeners and initializing the API for the initializer.
   */
  static async mountImmediately(
    initializer: Initializer<any>,
    options?: { [key: string]: any }
  ) {
    initializer.listeners?.(options);
          await initializer.init?.({
        imageParamsKeyMap: initializers._imageParamsKeyMap,
        globalLocale: initializers._globalLocale,
        ...options,
      });
  }

  /**
   * Mounts all registered initializers. This involves binding the event listeners and initializing the APIs for each initializer, in that order.
   */
  static mount() {
    initializers._mounted = true;
    // In this specific order
    // 1. Bind events
    initializers._initializers?.forEach(([initializer, options]) => {
      initializer.listeners?.(options);
    });

    // 2. Initialize APIs
    initializers._initializers?.forEach(([initializer, options]) => {
      initializer.init?.({
        imageParamsKeyMap: initializers._imageParamsKeyMap,
        globalLocale: initializers._globalLocale,
        ...options,
      });
    });
  }
  /**
   * Sets the image parameter keys. These keys are used when initializing the APIs for the initializers.
   * @param params - The image parameter keys.
   */
  static setImageParamKeys(params: { [key: string]: any }) {
    initializers._imageParamsKeyMap = params;
  }

  /**
   * Sets the global locale. This locale is used by components that need consistent formatting regardless of the user's browser locale.
   * @param locale - The locale string (e.g., 'en-US', 'es-MX', 'fr-FR').
   */
  static setGlobalLocale(locale: string) {
    initializers._globalLocale = locale;
  }
}
